import React from 'react';
import { Alert, CircularProgress } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { es as esLocale } from 'date-fns/locale';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { CloseConfirmationModal } from '../../modals/confirmation';
import { ConfirmationModal } from '../../ui/Modal';
import * as S from '../../sales/orders/ExchangeModal/ExchangeDrawer.styles';
import type { ClientCreateDrawerProps } from './clientCreateDrawer.types';
import { CLIENT_DRAWER_TOTAL_STEPS } from './clientCreateDrawer.types';
import { useClientCreateDrawer } from './useClientCreateDrawer';
import ClientCreateDrawerFormStep from './ClientCreateDrawerFormStep';
import ClientCreateDrawerReviewStep from './ClientCreateDrawerReviewStep';

export type { ClientDrawerFormSeed, ClientCreateDrawerProps } from './clientCreateDrawer.types';

const ClientCreateDrawer: React.FC<ClientCreateDrawerProps> = (props) => {
  const { open, onClose, mode = 'create' } = props;

  const d = useClientCreateDrawer(props);

  useEscapeKey(
    () => {
      if (open && !d.isSubmitting && !d.showCancelConfirmation && !d.showSaveConfirmModal) {
        d.handleCloseClick();
      }
    },
    open && !d.isSubmitting && !d.showCancelConfirmation && !d.showSaveConfirmModal,
  );

  if (!open) return null;

  const isEditMode = mode === 'edit';
  const isViewMode = d.isViewMode;

  const showStepProgress = !isViewMode;
  const localTotalSteps = CLIENT_DRAWER_TOTAL_STEPS;
  const activeStep = d.currentStep === 'FORM' ? 1 : 2;
  const progress = Math.round((activeStep / localTotalSteps) * 100);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
        <S.StyledDrawer
          anchor="right"
          open={open}
          onClose={d.handleCloseClick}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: '860px', md: '920px' },
              maxWidth: '920px',
            },
          }}
        >
          <S.DrawerContainer>
            <S.DrawerHeader
              sx={{
                background: 'linear-gradient(to right, #4f46e5, #a21caf)',
                borderBottom: 'none',
                color: '#ffffff',
              }}
            >
              <S.DrawerTitle sx={{ color: '#ffffff' }}>
                <PersonAddIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                {isViewMode
                  ? 'Detalle de cliente'
                  : isEditMode
                    ? 'Editar cliente'
                    : 'Nuevo cliente'}
              </S.DrawerTitle>
              <S.CloseButton
                onClick={d.handleCloseClick}
                disabled={d.isSubmitting}
                sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
              >
                <CloseIcon />
              </S.CloseButton>
            </S.DrawerHeader>

            {showStepProgress ? (
              <S.ProgressContainer>
                <S.ProgressBar>
                  <S.ProgressBarFill style={{ width: `${progress}%` }} />
                </S.ProgressBar>
                <S.ProgressInfo>
                  <span>
                    Paso {activeStep} de {localTotalSteps}
                  </span>
                  <span>{progress}% completado</span>
                </S.ProgressInfo>
              </S.ProgressContainer>
            ) : null}

            <S.ContentContainer>
              {d.error != null && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => d.setError(null)}>
                  {d.error}
                </Alert>
              )}

              {d.currentStep === 'FORM' && !isViewMode ? (
                <ClientCreateDrawerFormStep
                  form={d.form}
                  setForm={d.setForm}
                  isSubmitting={d.isSubmitting}
                  nameError={d.nameError}
                  emailError={d.emailError}
                  identityDocumentError={d.identityDocumentError}
                  phoneError={d.phoneError}
                  dateOfBirthAsDate={d.dateOfBirthAsDate}
                  minBirthDate={d.minBirthDate}
                  maxBirthDate={d.maxBirthDate}
                  referenceBirthDate={d.referenceBirthDate}
                />
              ) : (
                <ClientCreateDrawerReviewStep
                  form={d.form}
                  isViewMode={isViewMode}
                  dateOfBirthDisplay={d.dateOfBirthDisplay}
                  salesHistory={{
                    show: isViewMode,
                    historyItems: d.historyItems,
                    historyLoading: d.historyLoading,
                    historyError: d.historyError,
                    historyPage: d.historyPage,
                    historyTotal: d.historyTotal,
                    historyTotalPages: d.historyTotalPages,
                    onRetry: () => {
                      void d.loadSalesHistory(d.historyPage);
                    },
                    onGoToOrder: d.handleGoToOrder,
                    onPrevPage: () => {
                      void d.loadSalesHistory(d.historyPage - 1);
                    },
                    onNextPage: () => {
                      void d.loadSalesHistory(d.historyPage + 1);
                    },
                  }}
                />
              )}
            </S.ContentContainer>

            <S.DrawerFooter
              sx={{
                py: 1.25,
                px: 2,
                gap: 1,
              }}
            >
              <S.PreviousButton
                onClick={
                  isViewMode
                    ? onClose
                    : d.currentStep === 'FORM'
                      ? d.handleCloseClick
                      : d.handlePreviousStep
                }
                disabled={d.isSubmitting}
              >
                {isViewMode ? 'Cerrar' : d.currentStep === 'FORM' ? 'Cancelar' : 'Anterior'}
              </S.PreviousButton>
              <S.FooterButtonsContainer>
                {!isViewMode && d.currentStep === 'FORM' ? (
                  <S.NextButton
                    variant="contained"
                    onClick={d.handleNextStep}
                    disabled={d.isSubmitting}
                    endIcon={<NavigateNextIcon />}
                  >
                    Siguiente
                  </S.NextButton>
                ) : !isViewMode ? (
                  <S.ConfirmButton
                    variant="contained"
                    onClick={d.handleOpenSaveConfirmation}
                    disabled={d.isSubmitting}
                    startIcon={
                      d.isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CheckCircleIcon />
                      )
                    }
                  >
                    {d.isSubmitting
                      ? 'Guardando...'
                      : isEditMode
                        ? 'Guardar cambios'
                        : 'Guardar cliente'}
                  </S.ConfirmButton>
                ) : null}
              </S.FooterButtonsContainer>
            </S.DrawerFooter>
          </S.DrawerContainer>
        </S.StyledDrawer>
      </LocalizationProvider>

      <CloseConfirmationModal
        open={d.showCancelConfirmation}
        onClose={() => d.setShowCancelConfirmation(false)}
        onConfirm={d.cancelOperation}
      />

      <ConfirmationModal
        open={d.showSaveConfirmModal}
        onClose={d.handleCancelSaveConfirmation}
        onConfirm={() => void d.handleSubmit()}
        title={isEditMode ? '¿Confirmar cambios?' : '¿Confirmar registro de cliente?'}
        message={
          isEditMode ? '¿Desea guardar los cambios de este cliente?' : '¿Desea crear este cliente?'
        }
        confirmText="Confirmar"
        cancelText="Cancelar"
        icon={<CheckCircleIcon />}
        variant="default"
        loading={d.isSubmitting}
      />
    </>
  );
};

export default ClientCreateDrawer;
